import { useId, useMemo, useState } from 'react';
import { t, type Idioma } from '@matias-asset-os/i18n';
import type { Rol } from '@matias-asset-os/contracts';
import { api } from '../api.js';
import { Icono } from '../componentes/Iconos.js';
import { Modal } from '../componentes/Modal.js';
import { responsables, ZONAS } from './datos.js';
import { Detalle } from './Detalle.jsx';
import { useObras } from './useObras.js';
import { FichasDeCampo } from './FichasDeCampo.js';
import { useFichasDeCampo } from './useFichasDeCampo.js';
import { FichaRapida } from './FichaRapida.jsx';
import {
  aplicar,
  contar,
  DIAS_FIN_PROXIMA,
  FILTROS_VACIOS,
  hayFiltros,
  ordenar,
  type Filtros,
  type Orden,
} from './filtros.js';
import { Formulario } from './Formulario.jsx';
import { Lista, SelectorOrden } from './Lista.jsx';
import { MapaTeselas } from './MapaTeselas.js';
import { ETAPAS, FASES, TIPOS_PROYECTO, type Obra } from './modelo.js';

/**
 * Oportunidades de obra: la pantalla.
 *
 * El mapa y la lista beben del MISMO filtrado y del MISMO orden, y los
 * contadores cuentan sobre ese mismo resultado. No hay tres fuentes de verdad:
 * hay una, y tres formas de mirarla.
 *
 * Al abrir una obra y volver, la busqueda, los filtros, el orden y la vista
 * siguen donde estaban. Perderlos obligaria a rehacer el trabajo de encuadre
 * cada vez que se mira una ficha, que es lo que hace que la gente deje de
 * filtrar y se ponga a buscar a ojo.
 */

interface Props {
  idioma: Idioma;
  rol: Rol;
  nombreUsuario: string;
  onVolver: () => void;
}

type Vista =
  | { pantalla: 'mapa' | 'lista' }
  | { pantalla: 'detalle'; obraId: string; pestana?: 'fotos' }
  | { pantalla: 'nueva' }
  | { pantalla: 'editar'; obraId: string }
  /* La rutina diaria: se sueltan las fotos de las fichas del dia. */
  | { pantalla: 'fichas' };

/**
 * Los tres papeles que el encargo pide poder VER funcionando.
 *
 * `DIRECCION` lo ve y lo toca todo. `COMERCIAL` lo ve todo pero solo edita lo
 * que tiene asignado —abrir una obra de otro se queda en solo lectura, que es
 * la «navegacion directa a una oportunidad no autorizada»—. `OTRO` no entra.
 *
 * SE ELIGE A MANO, y esta ahi por una razon concreta: en la aplicacion real el
 * CRM es solo de Direccion, asi que sin este selector los otros dos estados no
 * se podrian ensenar nunca. No es un permiso: es una maqueta de un permiso.
 */
const PAPELES = ['DIRECCION', 'COMERCIAL', 'OTRO'] as const;
type Papel = (typeof PAPELES)[number];

const CLAVE_PAPEL: Record<Papel, string> = {
  DIRECCION: 'obras.rolDireccion',
  COMERCIAL: 'obras.rolComercial',
  OTRO: 'obras.rolOtro',
};

function papelDe(rol: Rol): Papel {
  if (rol === 'DIRECTOR') return 'DIRECCION';
  if (rol === 'MANAGER') return 'COMERCIAL';
  return 'OTRO';
}

export function Obras({ idioma, rol, nombreUsuario, onVolver }: Props) {
  const { estado, despachar, cargando, errorGuardado, descartarError } = useObras();
  const [filtros, setFiltros] = useState<Filtros>(FILTROS_VACIOS);
  const [orden, setOrden] = useState<Orden>('PUNTUACION');
  const [vista, setVista] = useState<Vista>({ pantalla: 'mapa' });
  const [seleccionada, setSeleccionada] = useState<string | null>(null);
  const [panelFiltros, setPanelFiltros] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [convirtiendo, setConvirtiendo] = useState<string | null>(null);
  /*
    Lo leido de una ficha de papel, esperando a que alguien lo repase. La foto
    viaja con el borrador: cuando el alta se guarda, se adjunta a la obra, que
    es de donde sale la respuesta a «de donde salio este telefono».
  */
  const [deFicha, setDeFicha] = useState<{ obra: Obra; foto: File; fichaId: string } | null>(null);
  /*
    El taco de fichas del dia vive aqui, no en su pantalla: repasar una lleva al
    formulario de alta, y con el taco dentro de la pantalla las demas fichas se
    perdian al volver —habia que volver a fotografiarlas y a leerlas—.
  */
  const taco = useFichasDeCampo(idioma);

  const [papel, setPapel] = useState<Papel>(() => papelDe(rol));

  const hoy = new Date().toISOString().slice(0, 10);

  /** Un comercial solo edita lo suyo; la direccion, todo. */
  const puedeEditarObra = (o: Obra | null) =>
    papel === 'DIRECCION' || (papel === 'COMERCIAL' && o?.responsable === nombreUsuario);
  const puedeCrear = papel === 'DIRECCION' || papel === 'COMERCIAL';

  const visibles = useMemo(
    () => ordenar(aplicar(estado.obras, filtros, hoy), orden, hoy),
    [estado.obras, filtros, orden, hoy],
  );
  const cuentas = useMemo(() => contar(visibles, hoy), [visibles, hoy]);

  const selectorPapel = (
    <label className="campo campo-papel">
      <span className="etiqueta-campo">{t(idioma, 'obras.simularRol')}</span>
      <select value={papel} onChange={(e) => setPapel(e.target.value as Papel)}>
        {PAPELES.map((p) => (
          <option key={p} value={p}>
            {t(idioma, CLAVE_PAPEL[p])}
          </option>
        ))}
      </select>
    </label>
  );

  // --- Acceso ---------------------------------------------------------------
  if (papel === 'OTRO') {
    return (
      <main>
        <div className="cabecera-seccion">
          <div>
            <h1>{t(idioma, 'obras.titulo')}</h1>
          </div>
          <div className="acciones">
            <button type="button" className="secundario" onClick={onVolver}>
              <Icono nombre="volver" /> {t(idioma, 'app.volver')}
            </button>
          </div>
        </div>
        <section className="tarjeta obras-denegado">
          <Icono nombre="candado" />
          <h2>{t(idioma, 'obras.accesoDenegado')}</h2>
          <p className="suave">{t(idioma, 'obras.accesoDenegadoPie')}</p>
          <p className="aviso-demo">{t(idioma, 'obras.permisoSimulado')}</p>
          {selectorPapel}
        </section>
      </main>
    );
  }

  const obraDe = (id: string) => estado.obras.find((o) => o.id === id) ?? null;

  // --- La rutina diaria: las fichas de papel --------------------------------
  if (vista.pantalla === 'fichas') {
    return (
      <FichasDeCampo
        idioma={idioma}
        taco={taco}
        onVolver={() => setVista({ pantalla: 'mapa' })}
        onRevisar={(ficha, lectura) => {
          setDeFicha({ obra: lectura.obra, foto: ficha.fichero, fichaId: ficha.id });
          setVista({ pantalla: 'nueva' });
        }}
      />
    );
  }

  // --- Alta y edicion -------------------------------------------------------
  if (vista.pantalla === 'nueva' || vista.pantalla === 'editar') {
    const enEdicion = vista.pantalla === 'editar' ? obraDe(vista.obraId) : undefined;
    return (
      <main>
        <Formulario
          {...(enEdicion ? { obra: enEdicion } : {})}
          {...(!enEdicion && deFicha ? { borrador: deFicha.obra } : {})}
          obras={estado.obras}
          idioma={idioma}
          quien={nombreUsuario}
          onCancelar={() => {
            // Volver de una ficha devuelve al taco, con ella todavia dentro:
            // dejarla a medias no es descartarla.
            const veniaDeFicha = deFicha !== null;
            setDeFicha(null);
            setVista({ pantalla: veniaDeFicha ? 'fichas' : 'mapa' });
          }}
          onAbrirExistente={(id) => setVista({ pantalla: 'detalle', obraId: id })}
          onGuardar={async (obra, fotos) => {
            if (vista.pantalla === 'editar') {
              void despachar({
                tipo: 'editar',
                obraId: obra.id,
                cambios: obra,
                quien: nombreUsuario,
                apunte: t(idioma, 'obras.guardarCambios'),
              });
              setVista({ pantalla: 'detalle', obraId: obra.id });
              return;
            }

            /*
              LAS FOTOS SE CUELGAN CUANDO LA OBRA YA EXISTE. Antes la foto de la
              ficha de papel se subia en el mismo instante en que se daba el alta,
              sin esperar a que el servidor tuviera la obra: la rechazaba por
              «no encontrada» y el error se tragaba en silencio. Ahora se espera
              al guardado, y luego se suben la ficha y las fotos elegidas.

              Si alguna foto falla no se deshace el alta: la obra ya vale, y se
              avisa de cuantas fotos no han entrado.
            */
            const guardada = await despachar({ tipo: 'crear', obra });
            const aSubir = [...(deFicha ? [deFicha.foto] : []), ...fotos];
            // Dada de alta, su ficha sale del taco: las demas siguen esperando.
            if (guardada && deFicha) taco.quitar(deFicha.fichaId);
            setDeFicha(null);
            let fallidas = 0;
            if (guardada) {
              for (const foto of aSubir) {
                try {
                  await api.subirAdjunto('OBRA', obra.id, foto);
                } catch {
                  fallidas += 1;
                }
              }
            } else {
              fallidas = aSubir.length;
            }
            if (fallidas > 0) setAviso(t(idioma, 'fotos.noSubieron', { total: fallidas }));
            setVista({
              pantalla: 'detalle',
              obraId: obra.id,
              ...(fotos.length > 0 ? { pestana: 'fotos' as const } : {}),
            });
          }}
        />
      </main>
    );
  }

  // --- Ficha completa -------------------------------------------------------
  if (vista.pantalla === 'detalle') {
    const obra = obraDe(vista.obraId);
    if (!obra) {
      return (
        <main>
          <section className="tarjeta">
            <p>{t(idioma, 'obras.sinResultados')}</p>
            <button type="button" onClick={() => setVista({ pantalla: 'mapa' })}>
              {t(idioma, 'obras.volverLista')}
            </button>
          </section>
        </main>
      );
    }
    return (
      <main>
        {!puedeEditarObra(obra) && (
          <p className="aviso-demo" role="status">
            {t(idioma, 'obras.soloLectura')}
          </p>
        )}
        <Detalle
          obra={obra}
          idioma={idioma}
          hoy={hoy}
          quien={nombreUsuario}
          puedeEditar={puedeEditarObra(obra)}
          onVolver={() => setVista({ pantalla: 'mapa' })}
          onEditar={() => setVista({ pantalla: 'editar', obraId: obra.id })}
          onConvertir={() => setConvirtiendo(obra.id)}
          despachar={despachar}
          {...(vista.pestana ? { pestanaInicial: vista.pestana } : {})}
        />
        {convirtiendo === obra.id && (
          <Conversion
            obra={obra}
            idioma={idioma}
            onCerrar={() => setConvirtiendo(null)}
            onConvertir={(cliente, propiedad) => {
              despachar({ tipo: 'convertir', obraId: obra.id, cliente, propiedad, quien: nombreUsuario });
              setConvirtiendo(null);
              setAviso(t(idioma, 'obras.convertida', { cliente, propiedad }));
            }}
          />
        )}
      </main>
    );
  }

  // --- Mapa y lista ---------------------------------------------------------
  /*
    LA FICHA SE CIERRA SI SU OBRA DEJA DE PASAR EL FILTRO.

    Al filtrar por «acciones vencidas» el marcador desaparecia del mapa pero su
    ficha se quedaba abierta al lado, ensenando una obra que ya no esta en lo
    que se esta mirando. Se busca entre las VISIBLES, no entre todas.
  */
  const obraSeleccionada = seleccionada
    ? (visibles.find((o) => o.id === seleccionada) ?? null)
    : null;

  const atajo = (cambios: Partial<Filtros>) => setFiltros((f) => ({ ...f, ...cambios }));

  return (
    <main className="obras-pantalla">
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'obras.titulo')}</h1>
          <p>{t(idioma, 'obras.sub')}</p>
        </div>
        <div className="acciones">
          <div className="conmutador-vista" role="group" aria-label={t(idioma, 'obras.mapa')}>
            <button
              type="button"
              aria-pressed={vista.pantalla === 'mapa'}
              title={t(idioma, 'obras.verMapa')}
              onClick={() => setVista({ pantalla: 'mapa' })}
            >
              <Icono nombre="pin" /> {t(idioma, 'obras.mapa')}
            </button>
            <button
              type="button"
              aria-pressed={vista.pantalla === 'lista'}
              title={t(idioma, 'obras.verLista')}
              onClick={() => setVista({ pantalla: 'lista' })}
            >
              <Icono nombre="tareas" /> {t(idioma, 'obras.lista')}
            </button>
          </div>
          <button type="button" className="secundario" onClick={onVolver}>
            <Icono nombre="volver" /> {t(idioma, 'app.volver')}
          </button>
          {puedeCrear && (
            <button type="button" className="secundario" onClick={() => setVista({ pantalla: 'fichas' })}>
              <Icono nombre="camara" /> {t(idioma, 'obras.fichas')}
            </button>
          )}
          {puedeCrear && (
            <button
              type="button"
              onClick={() => {
                setDeFicha(null);
                setVista({ pantalla: 'nueva' });
              }}
            >
              {t(idioma, 'obras.nueva')}
            </button>
          )}
        </div>
      </div>

      <p className="aviso-demo" role="note">
        {t(idioma, 'obras.simulacion')}
      </p>

      {aviso && (
        <p className="ok-mensaje" role="status">
          {aviso}
        </p>
      )}

      {/*
        GUARDAR PUEDE FALLAR, y entonces hay que decirlo. Una pantalla que
        acepta el cambio, lo pinta y lo pierde en silencio es peor que una que
        no lo acepta: nadie vuelve a comprobar lo que ya ha visto hecho.
      */}
      {errorGuardado && (
        <p className="error" role="alert">
          {t(idioma, 'obras.noSeGuardo', { obra: errorGuardado })}{' '}
          <button type="button" className="enlace" onClick={descartarError}>
            {t(idioma, 'tarea.cerrar')}
          </button>
        </p>
      )}

      {cargando && <p className="suave">{t(idioma, 'obras.cargando')}</p>}

      {/* --- Contadores: seis cifras, y cada una es un filtro --------------- */}
      <div className="obras-contadores">
        <Contador etiqueta={t(idioma, 'obras.cActivas')} valor={cuentas.activas} onClick={() => atajo({ soloActivas: true })} />
        <Contador etiqueta={t(idioma, 'obras.cPendientes')} valor={cuentas.pendientesContactar} onClick={() => atajo({ etapas: ['DETECTADA', 'INVESTIGANDO', 'PENDIENTE_CONTACTAR'] })} />
        <Contador etiqueta={t(idioma, 'obras.cContacto')} valor={cuentas.enContacto} onClick={() => atajo({ etapas: ['CONTACTADA', 'CONVERSACION', 'REUNION', 'PROPUESTA'] })} />
        <Contador etiqueta={t(idioma, 'obras.cFin')} valor={cuentas.finProxima} onClick={() => atajo({ finEnDias: DIAS_FIN_PROXIMA, soloActivas: true })} />
        <Contador etiqueta={t(idioma, 'obras.cVencidas')} valor={cuentas.vencidas} alerta onClick={() => atajo({ estadosAccion: ['VENCIDA'], soloActivas: true })} />
        <Contador etiqueta={t(idioma, 'obras.cAlta')} valor={cuentas.prioridadAlta} onClick={() => atajo({ puntuacionMin: 80 })} />
      </div>

      <div className="obras-barra">
        <input
          type="search"
          value={filtros.texto}
          placeholder={t(idioma, 'obras.buscar')}
          aria-label={t(idioma, 'obras.buscar')}
          onChange={(e) => setFiltros((f) => ({ ...f, texto: e.target.value }))}
        />
        <SelectorOrden orden={orden} idioma={idioma} onOrden={setOrden} />
        {selectorPapel}
        <button
          type="button"
          className="secundario"
          aria-expanded={panelFiltros}
          onClick={() => setPanelFiltros((v) => !v)}
        >
          <Icono nombre="embudo" /> {t(idioma, 'obras.filtros')}
        </button>
        {hayFiltros(filtros) && (
          <button type="button" className="fantasma" onClick={() => setFiltros(FILTROS_VACIOS)}>
            {t(idioma, 'obras.limpiar')}
          </button>
        )}
        <span className="suave numerico obras-cuenta">
          {t(idioma, 'obras.resultados', { total: visibles.length, sobre: estado.obras.length })}
        </span>
      </div>

      {panelFiltros && (
        <PanelFiltros
          filtros={filtros}
          obras={estado.obras}
          quien={nombreUsuario}
          idioma={idioma}
          onCambio={setFiltros}
        />
      )}

      {estado.obras.length === 0 ? (
        <section className="tarjeta obras-vacio">
          <h2>{t(idioma, 'obras.vacio')}</h2>
          <p className="suave">{t(idioma, 'obras.vacioPie')}</p>
        </section>
      ) : visibles.length === 0 ? (
        <section className="tarjeta obras-vacio">
          <h2>{t(idioma, 'obras.sinResultados')}</h2>
          <p className="suave">{t(idioma, 'obras.sinResultadosPie')}</p>
          <button type="button" className="secundario" onClick={() => setFiltros(FILTROS_VACIOS)}>
            {t(idioma, 'obras.limpiar')}
          </button>
        </section>
      ) : vista.pantalla === 'lista' ? (
        <Lista
          obras={visibles}
          idioma={idioma}
          hoy={hoy}
          seleccionada={seleccionada}
          orden={orden}
          onOrden={setOrden}
          onAbrir={(id) => setVista({ pantalla: 'detalle', obraId: id })}
        />
      ) : (
        <div className={`obras-mapa-zona${obraSeleccionada ? ' con-ficha' : ''}`}>
          <MapaTeselas
            obras={visibles}
            seleccionada={seleccionada}
            idioma={idioma}
            hoy={hoy}
            onSeleccionar={setSeleccionada}
            onAbrirDetalle={(id) => setVista({ pantalla: 'detalle', obraId: id })}
          />
          {obraSeleccionada && (
            <FichaRapida
              obra={obraSeleccionada}
              idioma={idioma}
              hoy={hoy}
              onCerrar={() => setSeleccionada(null)}
              onAbrir={(id) => setVista({ pantalla: 'detalle', obraId: id })}
            />
          )}
        </div>
      )}

      <div className="obras-leyenda">
        <b className="rotulo">{t(idioma, 'obras.leyenda')}</b>
        <span>
          <span className="leyenda-anillo" aria-hidden="true" /> {t(idioma, 'obras.leyendaAnillo')}
        </span>
        <span>
          <span className="leyenda-distintivo" aria-hidden="true">
            !
          </span>{' '}
          {t(idioma, 'obras.leyendaDistintivo')}
        </span>
      </div>
    </main>
  );
}

// --- Contador ----------------------------------------------------------------

function Contador({
  etiqueta,
  valor,
  alerta = false,
  onClick,
}: {
  etiqueta: string;
  valor: number;
  alerta?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`obras-contador${alerta && valor > 0 ? ' alerta' : ''}`}
      onClick={onClick}
    >
      <span className="rotulo">{etiqueta}</span>
      <b className="numerico">{valor}</b>
    </button>
  );
}

// --- Panel de filtros --------------------------------------------------------

function PanelFiltros({
  filtros,
  obras,
  quien,
  idioma,
  onCambio,
}: {
  filtros: Filtros;
  /** Para sacar de aqui por quien se puede filtrar, en vez de una lista fija. */
  obras: Obra[];
  quien: string;
  idioma: Idioma;
  onCambio: (f: Filtros) => void;
}) {
  const cambiar = (c: Partial<Filtros>) => onCambio({ ...filtros, ...c });
  const zonas = Object.keys(ZONAS);
  const municipios = [...new Set(Object.values(ZONAS).map((z) => z.municipio))];

  const alternar = <T,>(lista: T[], valor: T): T[] =>
    lista.includes(valor) ? lista.filter((x) => x !== valor) : [...lista, valor];

  return (
    <section className="tarjeta obras-filtros" aria-label={t(idioma, 'obras.filtros')}>
      <div className="filtro-grupo">
        <span className="rotulo">{t(idioma, 'obras.atajos')}</span>
        <div className="fila-pastillas">
          <button
            type="button"
            className="chip-tipo"
            aria-pressed={filtros.estadosAccion.includes('VENCIDA')}
            onClick={() => cambiar({ estadosAccion: alternar(filtros.estadosAccion, 'VENCIDA'), soloActivas: true })}
          >
            {t(idioma, 'obras.soloVencidas')}
          </button>
          <button
            type="button"
            className="chip-tipo"
            aria-pressed={filtros.puntuacionMin >= 80}
            onClick={() => cambiar({ puntuacionMin: filtros.puntuacionMin >= 80 ? 0 : 80 })}
          >
            {t(idioma, 'obras.soloAlta')}
          </button>
          <button
            type="button"
            className="chip-tipo"
            aria-pressed={filtros.finEnDias === DIAS_FIN_PROXIMA}
            onClick={() => cambiar({ finEnDias: filtros.finEnDias === DIAS_FIN_PROXIMA ? null : DIAS_FIN_PROXIMA })}
          >
            {t(idioma, 'obras.soloFin')}
          </button>
          <button
            type="button"
            className="chip-tipo"
            aria-pressed={filtros.etapas.includes('PENDIENTE_CONTACTAR')}
            onClick={() => cambiar({ etapas: alternar(filtros.etapas, 'PENDIENTE_CONTACTAR') })}
          >
            {t(idioma, 'obras.soloPendientes')}
          </button>
          <button
            type="button"
            className="chip-tipo"
            aria-pressed={filtros.soloActivas}
            onClick={() => cambiar({ soloActivas: !filtros.soloActivas })}
          >
            {t(idioma, 'obras.soloActivas')}
          </button>
        </div>
      </div>

      <div className="filtro-grupo">
        <span className="rotulo">{t(idioma, 'obras.zona')}</span>
        <div className="fila-pastillas">
          {zonas.map((z) => (
            <button
              key={z}
              type="button"
              className="chip-tipo"
              aria-pressed={filtros.zonas.includes(z)}
              onClick={() => cambiar({ zonas: alternar(filtros.zonas, z) })}
            >
              {z}
            </button>
          ))}
        </div>
      </div>

      <div className="filtro-grupo">
        <span className="rotulo">{t(idioma, 'obras.municipio')}</span>
        <div className="fila-pastillas">
          {municipios.map((m) => (
            <button
              key={m}
              type="button"
              className="chip-tipo"
              aria-pressed={filtros.municipios.includes(m)}
              onClick={() => cambiar({ municipios: alternar(filtros.municipios, m) })}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="filtro-grupo">
        <span className="rotulo">{t(idioma, 'obras.fase')}</span>
        <div className="fila-pastillas">
          {FASES.map((f) => (
            <button
              key={f}
              type="button"
              className="chip-tipo"
              aria-pressed={filtros.fases.includes(f)}
              onClick={() => cambiar({ fases: alternar(filtros.fases, f) })}
            >
              {t(idioma, `obras.fase.${f}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="filtro-grupo">
        <span className="rotulo">{t(idioma, 'obras.etapaComercial')}</span>
        <div className="fila-pastillas">
          {ETAPAS.map((e) => (
            <button
              key={e}
              type="button"
              className="chip-tipo"
              aria-pressed={filtros.etapas.includes(e)}
              onClick={() => cambiar({ etapas: alternar(filtros.etapas, e) })}
            >
              {t(idioma, `obras.etapa.${e}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="filtro-grupo">
        <span className="rotulo">{t(idioma, 'obras.tipoProyecto')}</span>
        <div className="fila-pastillas">
          {TIPOS_PROYECTO.map((tp) => (
            <button
              key={tp}
              type="button"
              className="chip-tipo"
              aria-pressed={filtros.tipos.includes(tp)}
              onClick={() => cambiar({ tipos: alternar(filtros.tipos, tp) })}
            >
              {t(idioma, `obras.tipo.${tp}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="filtro-grupo">
        <span className="rotulo">{t(idioma, 'obras.frescura.VERIFICAR')}</span>
        <div className="fila-pastillas">
          {(['RECIENTE', 'VERIFICAR', 'DESACTUALIZADA', 'SIN_VERIFICAR'] as const).map((fr) => (
            <button
              key={fr}
              type="button"
              className="chip-tipo"
              aria-pressed={filtros.frescuras.includes(fr)}
              onClick={() => cambiar({ frescuras: alternar(filtros.frescuras, fr) })}
            >
              {t(idioma, `obras.frescura.${fr}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="filtro-grupo">
        <span className="rotulo">{t(idioma, 'obras.responsable')}</span>
        <div className="fila-pastillas">
          {responsables(obras, quien).map((c) => (
            <button
              key={c}
              type="button"
              className="chip-tipo"
              aria-pressed={filtros.responsables.includes(c)}
              onClick={() => cambiar({ responsables: alternar(filtros.responsables, c) })}
            >
              {c}
            </button>
          ))}
          <button
            type="button"
            className="chip-tipo"
            aria-pressed={filtros.responsables.includes('')}
            onClick={() => cambiar({ responsables: alternar(filtros.responsables, '') })}
          >
            {t(idioma, 'obras.sinResponsable')}
          </button>
        </div>
      </div>

      <div className="filtro-grupo">
        <span className="rotulo">{t(idioma, 'obras.gSituacion')}</span>
        <div className="fila-pastillas">
          <button
            type="button"
            className="chip-tipo"
            aria-pressed={filtros.conPropietario === true}
            onClick={() => cambiar({ conPropietario: filtros.conPropietario === true ? null : true })}
          >
            {t(idioma, 'obras.propietarioConocido')}
          </button>
          <button
            type="button"
            className="chip-tipo"
            aria-pressed={filtros.enVenta === true}
            onClick={() => cambiar({ enVenta: filtros.enVenta === true ? null : true })}
          >
            {t(idioma, 'obras.yaEnVenta')}
          </button>
        </div>
      </div>
    </section>
  );
}

// --- Conversion simulada -----------------------------------------------------

function Conversion({
  obra,
  idioma,
  onCerrar,
  onConvertir,
}: {
  obra: Obra;
  idioma: Idioma;
  onCerrar: () => void;
  onConvertir: (cliente: string, propiedad: string) => void;
}) {
  const propietario = obra.contactos.find((c) => c.rol === 'PROPIETARIO');
  const [cliente, setCliente] = useState(propietario?.nombre ?? obra.nombre);
  const [propiedad, setPropiedad] = useState(obra.nombre);
  const [llevaContactos, setLlevaContactos] = useState(true);
  const [llevaHistorial, setLlevaHistorial] = useState(true);
  const [llevaObra, setLlevaObra] = useState(true);
  const ids = { cliente: useId(), propiedad: useId() };

  return (
    <Modal idioma={idioma} titulo={t(idioma, 'obras.convertirTitulo')} icono="edificio" onCerrar={onCerrar}>
      <p className="aviso-demo">{t(idioma, 'obras.convertirNota')}</p>

      <div className="campo">
        <label htmlFor={ids.cliente}>{t(idioma, 'obras.nombreCliente')}</label>
        <input id={ids.cliente} value={cliente} onChange={(e) => setCliente(e.target.value)} />
      </div>
      <div className="campo">
        <label htmlFor={ids.propiedad}>{t(idioma, 'obras.nombrePropiedad')}</label>
        <input id={ids.propiedad} value={propiedad} onChange={(e) => setPropiedad(e.target.value)} />
      </div>

      <span className="rotulo">{t(idioma, 'obras.queSeLleva')}</span>
      <label className="fila-flex pequeno">
        <input type="checkbox" checked={llevaContactos} onChange={(e) => setLlevaContactos(e.target.checked)} />
        {t(idioma, 'obras.llevaContactos')} ({obra.contactos.length})
      </label>
      <label className="fila-flex pequeno">
        <input type="checkbox" checked={llevaHistorial} onChange={(e) => setLlevaHistorial(e.target.checked)} />
        {t(idioma, 'obras.llevaHistorial')} ({obra.historial.length})
      </label>
      <label className="fila-flex pequeno">
        <input type="checkbox" checked={llevaObra} onChange={(e) => setLlevaObra(e.target.checked)} />
        {t(idioma, 'obras.llevaObra')}
      </label>

      <div className="acciones">
        <button type="button" className="secundario" onClick={onCerrar}>
          {t(idioma, 'obras.cancelar')}
        </button>
        <button
          type="button"
          disabled={cliente.trim().length < 2 || propiedad.trim().length < 2}
          onClick={() => onConvertir(cliente.trim(), propiedad.trim())}
        >
          {t(idioma, 'obras.confirmarConversion')}
        </button>
      </div>
    </Modal>
  );
}
